import { RotateCw } from 'lucide-react';
import { useState, type ReactElement } from 'react';

import { formatDateTime } from '@/lib/time';
import { CardFrame } from '@/components/ui/card-frame';
import { useToast } from '@/components/ui/toast';
import { usePermission } from '@/hooks/usePermission';
import { useDocumentCompanyAccess } from '@/lib/document-company';

import { retryError, syncRows, type SyncRow } from '../lms-sync-presentation';
import type { LmsSync, LmsSyncKind } from '../types';
import { useRetryLmsSync } from '../use-document-registration';

// Envíos del documento al DMS del LMS en la ficha (G3-4 creación desde la plantilla, G3-5
// baja): estado, motivo y, cuando el sistema ya no insiste, "Reintentar" para Calidad una
// vez corregida la causa. Solo aparece si hubo algún envío. La mutación va a la empresa
// activa: en una ficha de otra empresa solo se muestra.

export function LmsSyncCard({
  entry,
  sync,
}: {
  entry: { id: string; companyId: string; code: string };
  sync: LmsSync;
}): ReactElement | null {
  const canRegister = usePermission('quality:document:register');
  const access = useDocumentCompanyAccess(entry.companyId);
  const retry = useRetryLmsSync(entry.id);
  const { showToast } = useToast();
  const [busy, setBusy] = useState<LmsSyncKind | null>(null);
  const [error, setError] = useState<string | null>(null);
  const rows = syncRows(sync);
  if (rows.length === 0) {
    return null;
  }
  const canRetry = canRegister && access.kind === 'active';

  async function onRetry(row: SyncRow): Promise<void> {
    if (busy !== null) {
      return;
    }
    setBusy(row.kind);
    setError(null);
    try {
      await retry.mutateAsync(row.kind);
      showToast(`${row.label} de ${entry.code}: se reintentará en unos minutos`);
    } catch (err) {
      setError(retryError(err));
    } finally {
      setBusy(null);
    }
  }

  return (
    <CardFrame title="Envíos al LMS" className="d3">
      <ul className="ev-list">
        {rows.map((row) => (
          <li key={row.kind} className="ev-item">
            <div className="ev-head">
              <strong>{row.label}</strong>
              <span className={`badge ${row.badge.tone}`}>{row.badge.text}</span>
              <span className="soft">{formatDateTime(row.updatedAt)}</span>
            </div>
            {row.detail !== null && <p className="ev-text">{row.detail}</p>}
            {row.retryable && canRetry && (
              <button
                type="button"
                className="btn mut sm"
                disabled={busy !== null}
                aria-label={`Reintentar ${row.label.charAt(0).toLowerCase()}${row.label.slice(1)}`}
                onClick={() => void onRetry(row)}
              >
                <RotateCw size={14} aria-hidden="true" />
                {busy === row.kind ? 'Reintentando…' : 'Reintentar'}
              </button>
            )}
          </li>
        ))}
      </ul>
      {error !== null && (
        <div className="cb">
          <p className="fld-error" role="alert">
            {error}
          </p>
        </div>
      )}
    </CardFrame>
  );
}
